import { useState } from 'react'
import { Loader2, Send, AlertTriangle, Info, ChevronDown, ChevronRight } from 'lucide-react'
import { reachSummary, unreachableDoctors } from '../../lib/broadcast.js'
import { doctorCode, doctorFullName } from '../../lib/dentist.js'

/**
 * 🔑 The honest reach counter — the single most important thing on the composer.
 *
 * Measured live 2026-08-20: of 406 active doctors, 397 have a working account
 * and 9 do not. (An earlier estimate of ~110 came from a stale June CSV and was
 * wrong.) Of the 397, 306 hold a credential and 100 have an account they have
 * never set up — but that distinction is NOT computable live, because
 * `user.password` is access:internal and invisible to a Xano function stack. So
 * `can_login` means "has an active account", which is the gate the portal
 * actually enforces, and the panel says exactly that and nothing stronger.
 *
 * Delivery is portal-only by the owner's decision (2026-08-20) — no email leg.
 * So a composer that shows only "ส่งถึง 137 ท่าน" would be actively misleading:
 * the manager would send a shift request believing 137 doctors saw it and wonder
 * why nobody answered. This panel exists so the number they act on is the number
 * that is true, and so the ones who cannot see it are nameable rather than
 * abstract.
 *
 * The [ดูรายชื่อ] list is also the hook for the follow-up nobody has built yet:
 * a clinic-side bulk invite for doctors. Until that exists this list is the only
 * place the gap is visible.
 */
export default function ReachPanel({ preview, loading, error }) {
  const [open, setOpen] = useState(false)

  if (error) {
    return (
      <div className="rounded-xl border border-rose-200 bg-rose-50 px-3 py-2.5 text-[13px] text-rose-700">
        คำนวณผู้รับไม่สำเร็จ — {error}
      </div>
    )
  }

  if (loading) {
    return (
      <div className="rounded-xl border border-slate-200 bg-slate-50 px-3 py-2.5 flex items-center gap-2 text-[13px] text-slate-500">
        <Loader2 size={14} className="animate-spin" />
        กำลังคำนวณผู้รับ…
      </div>
    )
  }

  if (!preview) {
    return (
      <div className="rounded-xl border border-dashed border-slate-200 px-3 py-2.5 text-[13px] text-slate-400">
        เลือกผู้รับเพื่อดูจำนวนที่ส่งถึง
      </div>
    )
  }

  const r = reachSummary(preview)
  const cannot = unreachableDoctors(preview)

  return (
    <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
      <div className="px-3 py-2.5 flex items-start gap-2.5">
        <Send size={15} strokeWidth={1.75} className="mt-0.5 shrink-0 text-brand-600" />
        <div className="min-w-0 flex-1">
          <p className="text-[14px] font-semibold text-slate-900">{r.headline}</p>
          {r.total > 0 && (
            <p className="text-[12.5px] text-slate-500 mt-0.5">
              ประกาศนี้แสดงเฉพาะในระบบของคุณหมอ — ไม่มีการส่งอีเมลหรือ LINE
            </p>
          )}
        </div>
      </div>

      {/* Only rendered when non-zero. An all-reachable send must not carry a
          warning that says "0 people cannot see this" — that trains the eye to
          skip the banner on the day it matters. */}
      {r.warning && (
        <div className="border-t border-amber-100 bg-amber-50 px-3 py-2.5">
          <div className="flex items-start gap-2">
            <AlertTriangle size={14} strokeWidth={1.75} className="mt-0.5 shrink-0 text-amber-600" />
            <div className="min-w-0 flex-1">
              <p className="text-[13px] text-amber-900">{r.warning}</p>
              {cannot.length > 0 && (
                <button
                  type="button"
                  onClick={() => setOpen((v) => !v)}
                  className="mt-1 inline-flex items-center gap-1 text-[12.5px] font-medium text-amber-800 hover:text-amber-900"
                >
                  {open ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
                  ดูรายชื่อ {cannot.length} ท่าน
                </button>
              )}
              {open && (
                <ul className="mt-2 max-h-52 overflow-y-auto space-y-0.5 pr-1">
                  {cannot.map((d) => (
                    <li key={d.doctor_id} className="text-[12.5px] text-amber-900/90 flex items-baseline gap-2">
                      <span className="truncate">{doctorCode(d) || doctorFullName(d) || `หมอ #${d.doctor_id}`}</span>
                      <span className="ml-auto shrink-0 text-[11.5px] text-amber-700/70">
                        {d.user_id ? 'บัญชีปิดอยู่' : 'ยังไม่มีบัญชี'}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </div>
      )}

      {/* The ไม่ระบุ bucket — doctors with an empty spec[] that a spec-targeted
          send cannot reach. Measured 2026-08-20 this is 0 (every active doctor
          has a spec), so it normally never renders; it exists so a future
          spec-less doctor cannot vanish from a send silently. */}
      {r.skippedNote && (
        <div className="border-t border-slate-100 bg-slate-50 px-3 py-2 flex items-start gap-2">
          <Info size={13} strokeWidth={1.75} className="mt-0.5 shrink-0 text-slate-400" />
          <p className="text-[12.5px] text-slate-600">{r.skippedNote}</p>
        </div>
      )}
    </div>
  )
}
